import React from 'react';
import { useModal } from 'react-modal-hook';
import { useDispatch, useSelector } from 'react-redux';
import { selectIsLoadingAPIRequest } from 'store/ApiClient';
import { DaySegments, deleteDaySegmentRequest } from 'store/Collections/DaySegments';
import { IStore } from 'store/types';
import { dayOptions, Days, IDaySegment } from 'types';
import { formatTimeRange } from 'utils/date';
import { AlertContent, ModalFactory, Text, Tooltip } from '../../../../../components';
import { StyledIconButton, StyledItem, StyledItemTextContainer } from '../../../styles';

const mapDays = (days?: Days[]) => {
  const displayDays: string[] = [];

  if (days?.length > 0) {
    dayOptions.forEach((dayItem) => {
      if (days.some((day) => day === dayItem.value)) {
        displayDays.push(` ${dayItem.label}`);
      }
    });
  }
  return displayDays;
};

interface IDaySegmentItemProps {
  daySegment: IDaySegment;
  editPermission?: boolean;
  deletePermission?: boolean;
  disabled?: boolean;
  readOnly?: boolean;
  onEdit?(): void;
}

export const DaySegmentItem: React.FC<IDaySegmentItemProps> = ({ daySegment, editPermission, deletePermission, disabled, readOnly, onEdit }) => {
  const dispatch = useDispatch();
  const isLoadingDelete = useSelector((state: IStore) => selectIsLoadingAPIRequest(state, DaySegments.DELETE_DAY_SEGMENT_REQUEST));

  const [showRemoveModal, hideRemoveModal] = useModal(() => {
    const handleDelete = () => {
      dispatch(deleteDaySegmentRequest(daySegment.siteId, daySegment.id, hideRemoveModal));
    };

    return (
      <ModalFactory customModal onClose={hideRemoveModal} isActive>
        <AlertContent
          title="Are you sure you want to remove this day segment?"
          leftActionLabel="Cancel"
          rightActionLabel="Remove"
          onClose={hideRemoveModal}
          onLeftAction={hideRemoveModal}
          onRightAction={handleDelete}
          rightActionAppearance="destructive"
          isLoading={isLoadingDelete}
        />
      </ModalFactory>
    );
  }, [daySegment, isLoadingDelete]);

  const permissionTooltip = (type: string) => (
    <Tooltip id={`no-${type}-permission`}>
      You do not have permission to {type} day segments.
      <br />
      Contact your supervisor for more information.
    </Tooltip>
  );

  return (
    <StyledItem data-cy="day-segment-item">
      <StyledItemTextContainer>
        <Text size="small" fontWeight="semibold">
          {daySegment?.name}
        </Text>
        <Text size="xxsmall">{`${mapDays(daySegment?.days)} • ${formatTimeRange(daySegment?.start, daySegment?.end)}`}</Text>
      </StyledItemTextContainer>
      {!readOnly && (
        <div>
          <StyledIconButton label="Edit" appearance="white" size="small" icon="Edit" data-cy="edit-button" onClick={onEdit} disabled={disabled || !editPermission} tooltipId="no-edit-permission" />
          <StyledIconButton label="Remove" onClick={showRemoveModal} appearance="white" size="small" icon="Close" data-cy="remove-button" disabled={!deletePermission} tooltipId="no-delete-permission" />
          {!editPermission && permissionTooltip('edit')}
          {!deletePermission && permissionTooltip('delete')}
        </div>
      )}
    </StyledItem>
  );
};
